import { useMutation, useQueryClient } from '@tanstack/react-query';
import ApiConfig from 'src/config/api-config';

interface SaveOnboardingPayload {
  uid: string;
  step: number;
  data: string | string[] | { timezone: string; weekly_schedule: any }; // slug for step 1, array of slugs/strings for 2 & 3, schedule for step 4
}

export function useSaveOnboardingStep() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: SaveOnboardingPayload) => {
      const { uid, step, data } = payload;

      if (!uid) {
        throw new Error('User UID is required to save onboarding progress');
      }

      const apiUrl = `${ApiConfig.CLOUD_FUNCTIONS_BASE_URL}${ApiConfig.TRAINER_SAVE_ONBOARDING_STEP}`;
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ uid, step, data }),
      });

      if (!response.ok) {
        throw new Error('Failed to save onboarding progress');
      }

      return response.json();
    },
    onSuccess: (_, variables) => {
      const queryKey = ['onboarding-state', (variables as any).uid];

      // Update the cache immediately so if they navigate back, they see the new data
      queryClient.setQueryData(queryKey, (oldData: any) => {
        if (!oldData || !oldData.onboarding) return oldData;

        const updatedOnboarding = { ...oldData.onboarding };
        if (variables.step === 1) {
          updatedOnboarding.step1_experience = variables.data;
        } else if (variables.step === 2) {
          updatedOnboarding.step2_client_types = variables.data;
        } else if (variables.step === 3) {
          updatedOnboarding.step3_offers = variables.data;
        } else if (variables.step === 4) {
          updatedOnboarding.step4_schedule = variables.data;
        }

        return {
          ...oldData,
          onboarding: updatedOnboarding,
        };
      });

      // Still invalidate so the app fetches the real updated state (including profile, lastCompletedStep, etc)
      queryClient.invalidateQueries({ queryKey });
    },
  });
}
